import React, { useState, useEffect, useCallback } from 'react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { sets, images, customFields } from '../utils/api';
import CoinSelector from './shared/CoinSelector';
import TagSelector from './shared/TagSelector';
import FieldSelector from './shared/FieldSelector';
import CoverImageSelectionModal from './CoverImageSelectionModal';
import AdvancedFilterSection from './shared/filters/AdvancedFilterSection';
import { toast } from 'react-toastify';

const FILTER_FIELDS = [
  { id: 'assayer', label: 'Assayer', type: 'text' },
  { id: 'cac_sticker', label: 'CAC Sticker', type: 'select', options: ['green', 'gold', 'none'] },
  { id: 'certification_number', label: 'Certification Number', type: 'text' },
  { id: 'certification_service_id', label: 'Certification Service', type: 'text' },
  { id: 'coin_details', label: 'Coin Details', type: 'text' },
  { id: 'composition', label: 'Composition', type: 'text' },
  { id: 'country', label: 'Country', type: 'text' },
  { id: 'current_pop_higher', label: 'Current Pop Higher', type: 'number' },
  { id: 'current_population', label: 'Current Population', type: 'number' },
  { id: 'denomination', label: 'Denomination', type: 'text' },
  { id: 'diameter', label: 'Diameter', type: 'number' },
  { id: 'die_variety', label: 'Die Variety', type: 'text' },
  { id: 'edge', label: 'Edge', type: 'text' },
  { id: 'grade', label: 'Grade', type: 'text' },
  { id: 'grading_cost', label: 'Grading Cost', type: 'number' },
  { id: 'list_price', label: 'List Price', type: 'number' },
  { id: 'metal', label: 'Metal', type: 'text' },
  { id: 'minor_variety', label: 'Minor Variety', type: 'text' },
  { id: 'mint_mark', label: 'Mint Mark', type: 'text' },
  { id: 'mintage', label: 'Mintage', type: 'text' },
  { id: 'notes', label: 'Notes', type: 'text' },
  { id: 'pcgs_number', label: 'PCGS Number', type: 'text' },
  { id: 'pop_higher_when_bought', label: 'Pop Higher When Bought', type: 'number' },
  { id: 'population_when_bought', label: 'Population When Bought', type: 'number' },
  { id: 'price_guide_value', label: 'Price Guide Value', type: 'number' },
  { id: 'provenance', label: 'Provenance', type: 'text' },
  { id: 'purchase_date', label: 'Purchase Date', type: 'date' },
  { id: 'purchase_price', label: 'Purchase Price', type: 'number' },
  { id: 'reference_number', label: 'Reference Number', type: 'text' },
  { id: 'shipping_cost', label: 'Shipping Cost', type: 'number' },
  { id: 'status', label: 'Status', type: 'text' },
  { id: 'storage_location', label: 'Storage Location', type: 'text' },
  { id: 'total_cost', label: 'Total Cost', type: 'number' },
  { id: 'variety', label: 'Variety', type: 'text' },
  { id: 'weight', label: 'Weight', type: 'number' },
  { id: 'year', label: 'Year', type: 'text' }
];

function EditSetModal({
  set,
  onClose,
  onUpdate,
  onDelete,
  onSortChange = () => {},
  sortPreferences = {
    grid: { field: 'year', direction: 'asc' },
    carousel: { field: 'year', direction: 'asc' },
    timeline: { field: 'year', direction: 'asc' },
    details: { field: 'year', direction: 'asc' }
  }
}) {
  const [selectedCoinIds, setSelectedCoinIds] = useState([]);
  const [coverImagePath, setCoverImagePath] = useState(null);
  const [coins, setCoins] = useState([]);
  const [setDetails, setSetDetails] = useState(null);
  const [showCoverImageModal, setShowCoverImageModal] = useState(false);
  const [availableCustomFields, setAvailableCustomFields] = useState([]);
  const [localSortPreferences, setLocalSortPreferences] = useState(sortPreferences);
  const [pendingChanges, setPendingChanges] = useState(() => {
    // Ensure we have a valid set before accessing properties
    if (!set?.id) {
      return {
        coverImage: null,
        sortPreferences: {},
        name: '',
        description: '',
        type: 'static',
        field_preferences: { visible_fields: [] },
        filter_criteria: {
          all_tags: [],
          any_tags: [],
          filters: []
        },
        coinIds: []
      };
    }

    return {
      coverImage: set.cover_image_path !== undefined ? set.cover_image_path : null,
      sortPreferences: {},
      name: set.name || '',
      description: set.description || '',
      type: set.type || 'static',
      field_preferences: set.field_preferences || { visible_fields: [] },
      filter_criteria: {
        all_tags: set.filter_criteria?.all_tags || [],
        any_tags: set.filter_criteria?.any_tags || [],
        filters: set.filter_criteria?.filters || []
      },
      coinIds: []
    };
  });


  // Sync local sort preferences with props when they change
  useEffect(() => {
    setLocalSortPreferences(sortPreferences);
  }, [sortPreferences]);

  // Load initial set data
  useEffect(() => {
    const loadSetData = async () => {
      if (!set?.id) {
        // Skip silently in development when component mounts twice
        return;
      }

      try {
        const details = await sets.get(set.id);
        setSetDetails(details);
        console.log('Set Details:', {
          id: details.id,
          cover_image: details.cover_image,
          type: typeof details.cover_image
        });
        
        const initialCoins = details.coins || [];
        setCoins(initialCoins);
        const initialCoinIds = initialCoins.map(coin => coin.id);
        setSelectedCoinIds(initialCoinIds);
        setCoverImagePath(details.cover_image?.file_path || null);
        if (details.sort_preferences) {
          setLocalSortPreferences(details.sort_preferences);
        }

        setPendingChanges(prev => ({
          ...prev,
          coinIds: initialCoinIds,
          type: details.type || 'static',
          field_preferences: details.field_preferences || { visible_fields: [] },
          filter_criteria: {
            all_tags: details.filter_criteria?.all_tags || [],
            any_tags: details.filter_criteria?.any_tags || [],
            filters: details.filter_criteria?.filters || []
          },
          coverImage: details.cover_image?.file_path || prev.coverImage
        }));
      } catch (err) {
        console.error('Failed to load set details:', err);
        toast.error('Failed to load set details');
        // Set default values to prevent undefined
        setCoins([]);
        setSelectedCoinIds([]);
        setPendingChanges(prev => ({
          ...prev,
          coinIds: [],
          type: set.type || 'static',
          field_preferences: set.field_preferences || { visible_fields: [] },
          filter_criteria: {
            all_tags: [],
            any_tags: [],
            filters: []
          }
        }));
      }
    };
    loadSetData();
  }, [set?.id]); // Only reload when set ID changes, safely handle undefined

  // Load custom fields
  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const fields = await customFields.getAll();
        setAvailableCustomFields(fields);
      } catch (err) {
        console.error('Failed to load custom fields:', err);
        toast.error('Failed to load custom fields');
      }
    };

    loadCustomFields();
  }, []);

  useEscapeKey(onClose);

  const handleSortChange = useCallback((view, field, direction) => {
    const currentPrefs = localSortPreferences[view] || { field: 'year', direction: 'asc' };
    const newPreferences = {
      field: field || currentPrefs.field,
      direction: direction || currentPrefs.direction
    };

    setLocalSortPreferences(prev => ({
      ...prev,
      [view]: newPreferences
    }));
    
    setPendingChanges(prev => ({
      ...prev,
      sortPreferences: {
        ...prev.sortPreferences,
        [view]: newPreferences
      }
    }));
  }, [localSortPreferences]);

  const handleCoinSelection = (newSelectedIds) => {
    setSelectedCoinIds(newSelectedIds);
    setPendingChanges(prev => ({
      ...prev,
      coinIds: newSelectedIds
    }));
  };

  const handleUpdateCover = useCallback((imagePath) => {
    setCoverImagePath(imagePath);
    setPendingChanges(prev => ({
      ...prev,
      coverImage: imagePath
    }));
  }, []);

  // Combine regular fields and custom fields for the filter section
  const allFields = [
    ...FILTER_FIELDS,
    ...availableCustomFields.map(field => ({
      id: `custom_field_${field.id}`,
      label: field.name,
      type: 'text'
    }))
  ];

  const handleSave = useCallback(async () => {
    try {
      // Save set details
      await sets.update(set.id, {
        name: pendingChanges.name,
        description: pendingChanges.description,
        type: pendingChanges.type,
        field_preferences: pendingChanges.field_preferences,
        filter_criteria: pendingChanges.type === 'dynamic' ? {
          all_tags: pendingChanges.filter_criteria.all_tags,
          any_tags: pendingChanges.filter_criteria.any_tags,
          filters: (() => {
            // Debug: Log raw filters
            console.log('Raw filters:', JSON.stringify(pendingChanges.filter_criteria.filters, null, 2));
            
            const processedFilters = pendingChanges.filter_criteria.filters
              .filter(filter => {
                // Debug: Log each filter's properties
                console.log('\nProcessing filter:', {
                  column: filter.column,
                  operator: filter.operator,
                  value: filter.value,
                  logicOperator: filter.logicOperator
                });
                
                const hasBasics = filter.column && filter.operator;
                const hasValue = filter.value && filter.value.trim() !== '';
                const hasLogicOp = filter.logicOperator;
                const isSpecialOp = ['isEmpty', 'isNotEmpty'].includes(filter.operator);
                
                console.log('Validation:', {
                  hasBasics,
                  hasValue,
                  hasLogicOp,
                  isSpecialOp,
                  willKeep: hasBasics && (hasValue || isSpecialOp || hasLogicOp)
                });
                
                return hasBasics && (hasValue || isSpecialOp || hasLogicOp);
              });
            
            // Debug: Log processed filters
            console.log('Processed filters:', JSON.stringify(processedFilters, null, 2));
            return processedFilters;
          })()
            .map((filter, index, arr) => {
              // Only remove logicOperator from the last filter
              if (index === arr.length - 1) {
                const { logicOperator, ...filterWithoutLogic } = filter;
                return filterWithoutLogic;
              }
              return filter;
            })
        } : null
      });

      // Update coins if they've been modified and set is static
      if (pendingChanges.type === 'static' && pendingChanges.coinIds && pendingChanges.coinIds.length > 0) {
        const currentCoins = await sets.get(set.id);
        const currentCoinIds = currentCoins.coins?.map(coin => coin.id) || [];
        
        const hasChanges = pendingChanges.coinIds.length !== currentCoinIds.length ||
          !pendingChanges.coinIds.every(id => currentCoinIds.includes(id));
        
        if (hasChanges) {
          const coinsToAdd = pendingChanges.coinIds.filter(id => !currentCoinIds.includes(id));
          const coinsToRemove = currentCoinIds.filter(id => !pendingChanges.coinIds.includes(id));
          
          if (coinsToAdd.length > 0) {
            await sets.addCoins(set.id, coinsToAdd);
          }
          if (coinsToRemove.length > 0) {
            await sets.removeCoins(set.id, coinsToRemove);
          }
        }
      }

      // Apply sort preferences if any view was changed
      if (Object.keys(pendingChanges.sortPreferences).length > 0) {
        await sets.updateSortPreferences(set.id, localSortPreferences);
        onSortChange(localSortPreferences);
      }

      // Apply cover image change if needed (after all other changes)
      if (pendingChanges.coverImage !== null) {
        await sets.updateCover(set.id, pendingChanges.coverImage);
        const event = new CustomEvent('coverChange', { detail: pendingChanges.coverImage });
        window.dispatchEvent(event);
      }

      toast.success('Changes saved successfully');
      // Call onUpdate first to refresh data
      if (onUpdate) {
        await onUpdate();
      }
      // Only dispatch setChange event if the set was deleted
      if (!set.id) {
        window.dispatchEvent(new Event('setChange'));
      }
      onClose();
    } catch (err) {
      console.error('Failed to save changes:', err);
      if (err.message === 'Set ID is required') {
        toast.error('Unable to save: Set ID is missing');
      } else if (err.message.includes('not found')) {
        toast.error('Unable to save: Set no longer exists');
      } else {
        toast.error(err.message || 'Failed to save changes');
      }
    }
  }, [set.id, pendingChanges, localSortPreferences, onClose, onSortChange]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex justify-between items-start px-6 py-4 border-b border-gray-200 bg-white shadow-sm">
          <h2 className="text-xl font-bold text-gray-900">Edit Set: {set.name}</h2>
          <button 
            id="close-modal"
            name="close-modal"
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* Set Type */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Set Type</h3>
            <div className="flex gap-4">
              <label htmlFor="set-type-static" className="inline-flex items-center">
                <input
                  type="radio"
                  id="set-type-static"
                  name="set-type"
                  value="static"
                  checked={pendingChanges.type === 'static'}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setPendingChanges(prev => ({
                      ...prev,
                      type: newType,
                      filter_criteria: newType === 'static' ? null : {
                        all_tags: [],
                        any_tags: [],
                        filters: []
                      }
                    }));
                  }}
                  className="form-radio h-4 w-4 text-indigo-600"
                />
                <span className="ml-2">Static Set</span>
              </label>
              <label htmlFor="set-type-dynamic" className="inline-flex items-center">
                <input
                  type="radio"
                  id="set-type-dynamic"
                  name="set-type"
                  value="dynamic"
                  checked={pendingChanges.type === 'dynamic'}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setPendingChanges(prev => ({
                      ...prev,
                      type: newType,
                      filter_criteria: newType === 'dynamic' ? {
                        all_tags: [],
                        any_tags: [],
                        filters: []
                      } : null
                    }));
                  }}
                  className="form-radio h-4 w-4 text-indigo-600"
                />
                <span className="ml-2">Dynamic Set</span>
              </label>
            </div>
          </div>

          {/* Name and Description */}
          <div>
            <div className="mb-4">
              <label htmlFor="set-name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                id="set-name"
                name="set-name"
                value={pendingChanges.name}
                onChange={(e) => setPendingChanges(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label htmlFor="set-description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                id="set-description"
                name="set-description"
                value={pendingChanges.description}
                onChange={(e) => setPendingChanges(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Field Preferences */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Visible Fields</h3>
            <FieldSelector
              id="field-selector"
              name="field-selector"
              selectedFields={pendingChanges.field_preferences?.visible_fields || []}
              onChange={(fields) => setPendingChanges(prev => ({
                ...prev,
                field_preferences: { visible_fields: fields }
              }))}
              className="mt-1"
              customFields={availableCustomFields}
            />
          </div>

          {pendingChanges.type === 'static' ? (
            /* Coin Selection for Static Sets */
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Coins</h3>
              <CoinSelector
                id="coin-selector"
                name="coin-selector"
                selectedCoinIds={selectedCoinIds}
                onChange={handleCoinSelection}
                className="mt-1"
              />
            </div>
          ) : (
            <>
              {/* Filter Criteria for Dynamic Sets */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Filter by Tags</h3>
                  <div className="mt-2 space-y-4">
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700">
                        Must Have ALL These Tags
                      </legend>
                      <div className="mt-2">
                          <TagSelector
                          prefix="all-"
                          selectedTagIds={pendingChanges.filter_criteria?.all_tags || []}
                          onChange={(tagIds) => setPendingChanges(prev => ({
                            ...prev,
                            filter_criteria: {
                              ...prev.filter_criteria,
                              all_tags: tagIds
                            }
                          }))}
                          className="mt-1"
                        />
                      </div>
                    </fieldset>

                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700">
                        Must Have ANY of These Tags
                      </legend>
                      <div className="mt-2">
                          <TagSelector
                          prefix="any-"
                          selectedTagIds={pendingChanges.filter_criteria?.any_tags || []}
                          onChange={(tagIds) => setPendingChanges(prev => ({
                            ...prev,
                            filter_criteria: {
                              ...prev.filter_criteria,
                              any_tags: tagIds
                            }
                          }))}
                          className="mt-1"
                        />
                      </div>
                    </fieldset>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Filters</h3>
                  <AdvancedFilterSection
                    fields={allFields}
                    filters={pendingChanges.filter_criteria?.filters || []}
                    onChange={(filters) => setPendingChanges(prev => ({
                      ...prev,
                      filter_criteria: {
                        ...prev.filter_criteria,
                        filters
                      }
                    }))}
                  />
                </div>
              </div>
            </>
          )}

          {/* Cover Image */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Cover Image</h3>
            {pendingChanges.coverImage ? (
              <div className="relative w-48 h-48 rounded-lg overflow-hidden cursor-pointer group" onClick={() => setShowCoverImageModal(true)}>
                <img 
                  src={images.getImage(setDetails?.cover_image?.thumbnail_path || pendingChanges.coverImage, setDetails?.cover_image?.source)} 
                  alt="Set cover"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    Change Cover Image
                  </span>
                </div>
              </div>
            ) : (
              <button
                id="select-cover-image"
                name="select-cover-image"
                onClick={() => setShowCoverImageModal(true)}
                className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-500 hover:text-gray-700 hover:border-gray-400"
              >
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" />
                  </svg>
                  <p className="mt-1">Select Cover Image</p>
                </div>
              </button>
            )}
          </div>

          {/* Cover Image Selection Modal */}
          {showCoverImageModal && (
            <CoverImageSelectionModal
              coins={coins}
              onSelect={(imagePath) => {
                handleUpdateCover(imagePath);
                setShowCoverImageModal(false);
              }}
              onClose={() => setShowCoverImageModal(false)}
              currentCoverImage={pendingChanges.coverImage}
            />
          )}

          {/* Sort Preferences */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Sort Preferences</h3>
            <div className="space-y-4">
              {['grid', 'carousel', 'timeline', 'details'].map(view => (
                <div key={view} className="flex items-center gap-2">
                  <div className="flex-1 flex gap-2">
                    <div className="flex-1">
                      <label 
                        htmlFor={`${view}-sort-field`}
                        className="block text-sm font-medium text-gray-700 capitalize mb-1"
                      >
                        {view} View Sort Field
                      </label>
                      <select
                        id={`${view}-sort-field`}
                        name={`${view}-sort-field`}
                        value={localSortPreferences[view]?.field || 'year'}
                        onChange={(e) => handleSortChange(view, e.target.value)}
                        className="w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md"
                      >
                        <option value="purchase_date">Purchase Date</option>
                        <option value="year">Year</option>
                        <option value="denomination">Denomination</option>
                        <option value="grade">Grade</option>
                      </select>
                    </div>
                    <div>
                      <label 
                        htmlFor={`${view}-sort-direction`}
                        className="block text-sm font-medium text-gray-700 capitalize mb-1"
                      >
                        Direction
                      </label>
                      <select
                        id={`${view}-sort-direction`}
                        name={`${view}-sort-direction`}
                        value={localSortPreferences[view]?.direction || 'asc'}
                        onChange={(e) => handleSortChange(view, null, e.target.value)}
                        className="w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 rounded-md min-w-[120px]"
                      >
                        <option value="asc">Ascending</option>
                        <option value="desc">Descending</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4 border-t border-gray-200 mt-6">
            <button
              id="delete-set"
              name="delete-set"
              onClick={onDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              Delete Set
            </button>
            <div className="flex gap-2">
              <button
                id="cancel-edit"
                name="cancel-edit"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                id="save-changes"
                name="save-changes"
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditSetModal;
